<template>
  <base-modal
    :ref="modal.key"
    :show="showModalOpen"
    :title="modalTitle"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
    :maxWidth="groupByContentType ? '60%' : '85%'"
  >
    <template v-slot:footer>
      <v-row class="pl-4">
        <v-col>
          <v-switch
            v-model="groupByContentType"
            v-bind:label="$t('GroupByContentType')"
            @change="onGroupingChanged"
          ></v-switch>
        </v-col>
        <v-col v-if="requiresCertificatePassword">
          <v-text-field
            v-bind:label="`${$t('Password')} (${$t('Certificate')})`"
            :append-icon="showCertificatePassword ? 'mdi-eye' : 'mdi-eye-off'"
            @click:append="showCertificatePassword = !showCertificatePassword"
            :type="showCertificatePassword ? 'text' : 'password'"
            v-model="certificatePassword"
          ></v-text-field>
        </v-col>
      </v-row>
    </template>
    <div>
      <v-tabs v-model="selectedTab" @change="handleTabChange">
        <v-tab
          v-for="(item, index) in groupByContentType
            ? ctGroupedItems
            : docGroupedItems"
          :key="index"
        >
          <div v-if="groupByContentType">
            {{ item.contentTypeName }}
          </div>
          <div v-if="!groupByContentType">
            <v-icon large class="mr-1">mdi-file-document-outline</v-icon>
            #{{ item.objectID }}
            <v-hover v-slot="{ hover }">
              <v-btn
                v-if="docGroupedItems.length > 1"
                icon
                small
                class="ml-1"
                :elevation="hover ? 4 : 0"
                @click.prevent="removeDocument(item.objectID)"
                ><v-icon small :color="hover ? 'error' : 'default'"
                  >mdi-close</v-icon
                >
              </v-btn>
            </v-hover>
          </div>
        </v-tab>
        <v-tab-item
          v-for="(item, index) in groupByContentType
            ? ctGroupedItems
            : docGroupedItems"
          :key="index"
        >
          <div class="mt-2">
            <v-row>
              <v-col>
                <v-row align="center">
                  <v-col>
                    <v-select
                      v-model="item.selectedFlow"
                      :items="item.availableFlows"
                      item-text="text"
                      item-value="id"
                      :label="$t('Flow')"
                      :readonly="item.availableFlows.length == 1"
                      @change="getActions(item, groupByContentType, !groupByContentType)"
                    >
                      <template v-slot:append-outer>
                        <div v-if="!groupByContentType">
                          <v-tooltip top>
                            <template v-slot:activator="{ on }">
                              <v-btn
                                v-on="on"
                                text
                                small
                                color="primary"
                                @click="showFlowDiagram(item.objectID)"
                              >
                                <v-icon>mdi-state-machine</v-icon>
                              </v-btn>
                            </template>
                            <span>{{ $t("FlowChart") }}</span>
                          </v-tooltip>
                        </div>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>
                <v-row justify="center" align="center" v-show="item.loading">
                  <div class="text-center">
                    <div>
                      <v-progress-circular
                        class="my-4"
                        :size="50"
                        width="5"
                        color="primary"
                        indeterminate
                      ></v-progress-circular>
                    </div>
                    <p class="display-1">{{ $t("Loading") }}...</p>
                  </div>
                </v-row>
                <v-card
                  outlined
                  v-if="!item.loading && item.actionFields.length > 0"
                >
                  <v-card-subtitle>{{ $t("Actions") }}</v-card-subtitle>
                  <v-card-text>
                    <v-row no-gutters>
                      <v-col
                        md="12"
                        v-for="(field, fieldIndex) in item.actionFields"
                        v-bind:key="`${
                          groupByContentType
                            ? item.contentTypeID
                            : item.objectID
                        }-${field.defFieldId}`"
                      >
                        <DocumentField
                          :ref="`${
                            groupByContentType
                              ? item.contentTypeID
                              : item.objectID
                          }-${field.code}`"
                          :field="field"
                          :documentId="0"
                          @on-field-change="onFieldChange(field, item)"
                          :disabled="field.isReadOnly"
                          :index="fieldIndex"
                          :ctIndex="index"
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
                <div v-if="transitionType == 'R'">
                  <v-row align="center">
                    <v-col>
                      <v-text-field
                        v-bind:label="$t('Comment')"
                        v-model="item.comment"
                        :error-messages="
                          groupByContentType
                            ? ctGroupedCommentErrors(index)
                            : docGroupedCommentErrors(index)
                        "
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </div>
              </v-col>
              <v-col v-if="!groupByContentType">
                <v-expansion-panels multiple accordion v-model="expandedPanels">
                  <v-expansion-panel @change="loadDocument(item.objectID)">
                    <v-expansion-panel-header>{{
                      $t("Document")
                    }}</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <PdfViewer
                        :ref="viewerKey(item.objectID)"
                        :height="getHeight"
                        :isSearchResult="false"
                    /></v-expansion-panel-content>
                  </v-expansion-panel>
                  <v-expansion-panel>
                    <v-expansion-panel-header>{{
                      $t("Fields")
                    }}</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <DocumentItemFields
                        :fieldList="getDisplayFields(item.objectID)"
                        :documentId="item.objectID"
                      />
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
          </div>
        </v-tab-item>
      </v-tabs>
      <FlowDiagramModal :ref="flowDiagramModalKey" />
    </div>
  </base-modal>
</template>
<script>
import DocumentService from '@/services/DocumentService';
import ValidationItem from '@/ecm/models/search/results/ValidationItem';
import GroupedValidationItem from '@/ecm/models/search/results/GroupedValidationItem';
import DocumentField from '@/components/document/documentModal/field/DocumentField';
import DocumentItemFields from '@/components/search/results/list/item/DocumentItemFields';
import FlowDiagramModal from '@/components/administration/flow/FlowDiagramModal.vue';
import Field from '@/ecm/models/document/Field';
import Utils from '@/ecm/common/Utils';
import PdfViewer from '@/components/document/PdfViewer';
import { requiredIf } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';

export default {
    components: {
        DocumentField,
        DocumentItemFields,
        PdfViewer,
        FlowDiagramModal,
    },
    name: 'ValidationModal',
    data() {
        return {
            validationItems: [],
            ctGroupedItems: [],
            docGroupedItems: [],
            modal: {
                title: this.$t('Flows'),
                key: 'validationModal',
            },
            showModalOpen: false,
            transitionType: 'V',
            conditionItems: [],
            fieldList: [],
            comment: '',
            groupByContentType: false,
            selectedTab: 0,
            loadedDocuments: [],
            documentList: [],
            flowDiagramModalKey: 'flowDiagramModalKey',
            certificatePassword: '',
            showCertificatePassword: false,
            expandedPanels: [0],
        };
    },
    mixins: [validationMixin],
    validations: {
        ctGroupedItems: {
            $each: {
                comment: {
                    required: requiredIf(function () {
                        return this.transitionType == 'R' && this.groupByContentType;
                    }),
                },
            },
        },
        docGroupedItems: {
            $each: {
                comment: {
                    required: requiredIf(function () {
                        return this.transitionType == 'R' && !this.groupByContentType;
                    }),
                },
            },
        },
    },
    computed: {
        modalInstance() {
            return this.$refs[this.modal.key];
        },
        requiresCertificatePassword() {
            return this.validationItems.some(
                (vi) => vi.requiresCertificatePassword == true
            );
        },
        flowDiagramModalInstance() {
            return this.$refs[this.flowDiagramModalKey];
        },
        modalTitle() {
            return this.transitionType == 'V'
                ? this.$t('Validate')
                : this.$t('Refuse');
        },
        getHeight() {
            return window.innerHeight * 0.6;
        },
    },
    watch: {
        selectedTab(val) {
            this.$nextTick(() => {
                if (!this.groupByContentType) {
                    var self = this;
                    setTimeout(() => {
                        self.refreshList(
                            self.docGroupedItems[val].actionFields.map((af) => af.code),
                            self.docGroupedItems[val]
                        );
                    }, 100);
                }
            });
        },
    },
    methods: {
        viewerKey(documentId) {
            return `documentValidationPdfViewer-${documentId}`;
        },
        onGroupingChanged(val) {
            this.$nextTick(() => {
                this.selectedTab = 0;
                this.loadedDocuments = [];
                if (!val) {
                    var self = this;
                    setTimeout(() => {
                        self.refreshList(
                            self.docGroupedItems[0].actionFields.map((af) => af.code),
                            self.docGroupedItems[0]
                        );
                        self.handleTabChange();
                    }, 100);
                }
            });
        },
        viewerInstance(documentId) {
            return this.$refs[this.viewerKey(documentId)][0];
        },
        documentFieldInstance(field, groupedItem) {
            var key = `${
                this.groupByContentType
                    ? groupedItem.contentTypeID
                    : groupedItem.objectID
            }-${field.code}`;
            return this.$refs[key];
        },
        removeDocument(documentId) {
            this.docGroupedItems = this.docGroupedItems.filter(
                (d) => d.objectID != documentId
            );
        },

        showFlowDiagram(documentId) {
            DocumentService.getFlowDiagrams(documentId).then((result) => {
                this.flowDiagramModalInstance.fromDocumentList(result);
            });
        },
        loadDocument(documentId) {
            if (this.groupByContentType) return;
            if (this.loadedDocuments.includes(documentId)) return;

            this.$nextTick(() => {
                var self = this;
                DocumentService.canDisplay(documentId).then(async (canDisplay) => {
                    let file = encodeURIComponent('/static/defaultDocument.pdf');
                    if (canDisplay) {
                        const fileUrl = await DocumentService.display(documentId);
                        file = encodeURIComponent(fileUrl);
                    }
                    self.viewerInstance(documentId).load(file);
                    self.loadedDocuments.push(documentId);
                });
            });
        },
        ctGroupedCommentErrors(index) {
            const errors = [];
            if (!this.$v.ctGroupedItems.$each[index].comment.$dirty) return errors;
            !this.$v.ctGroupedItems.$each[index].comment.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        docGroupedCommentErrors(index) {
            const errors = [];
            if (!this.$v.docGroupedItems.$each[index].comment.$dirty) return errors;
            !this.$v.docGroupedItems.$each[index].comment.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        onFieldChange(field, groupedItem) {
            if (field.fieldsThatDependOn.length > 0)
                this.$nextTick(() => {
                    this.refreshList(field.fieldsThatDependOn, groupedItem);
                });
        },
        refreshList(fieldCodesToRefresh, groupedItem) {
            for (let i = 0; i < fieldCodesToRefresh.length; i++) {
                var fieldCode = fieldCodesToRefresh[i];
                const fieldToRefresh = groupedItem.actionFields.find(
                    (f) => f.code == fieldCode
                );
                const parameters = [];
                for (let j = 0; j < fieldToRefresh.fieldsOnWhichDepends.length; j++) {
                    var paramCode = fieldToRefresh.fieldsOnWhichDepends[j];
                    var paramField = groupedItem.actionFields.find(
                        (f) => f.code == paramCode
                    );
                    if (!paramField) {
                        var docFields = this.getDisplayFields(groupedItem.objectID);
                        if (docFields) {
                            paramField = docFields.find((f) => f.code == paramCode);
                        }
                    }
                    parameters.push({
                        Name: paramCode,
                        Value: paramField ? paramField.value || '' : '',
                    });
                }
                this.documentFieldInstance(
                    fieldToRefresh,
                    groupedItem
                )[0].refreshListElements(parameters);
            }
        },
        init(data) {
            this.certificatePassword = '';
            this.showCertificatePassword = false;
            this.validationItems = data.items.map((x) => new ValidationItem(x));
            const ctIdList = [
                ...new Set(data.items.map((item) => item.ContentTypeID)),
            ];
            this.ctGroupedItems = data.ctGroupedItems.map(
                (x) => new GroupedValidationItem(x)
            );
            this.docGroupedItems = data.docGroupedItems.map(
                (x) => new GroupedValidationItem(x)
            );
            var self = this;
            for (let i = 0; i < ctIdList.length; i++) {
                var ctId = ctIdList[i];
                DocumentService.getStructure(ctId).then((dto) => {
                    var availableFields = dto.Fields.map(function (fieldDto) {
                        return new Field(fieldDto, dto.Fields);
                    });
                    self.ctGroupedItems
                        .filter((ctg) => ctg.contentTypeID == dto.ContentTypeID)
                        .forEach((item) => {
                            item.availableFields = availableFields;
                            self.getActions(item, true);
                        });
                    self.docGroupedItems
                        .filter((ctg) => ctg.contentTypeID == dto.ContentTypeID)
                        .forEach((item) => {
                            item.availableFields = availableFields;
                            self.getActions(item, false);
                        });
                });
            }
        },
        getDisplayFields(documentID) {
            var doc = this.documentList.find((d) => d.ObjectID == documentID);
            if (doc) {
                return doc.Fields.map(function (dto) {
                    return new Field(dto);
                });
            }
        },
        getActions(groupedValidationItem, groupByContentType, refreshList) {
            groupedValidationItem.loading = true;
            groupedValidationItem.actionFields = [];
            var arr = (
                groupByContentType
                    ? this.validationItems.filter(
                        (v) =>
                            v.contentTypeID == groupedValidationItem.contentTypeID &&
                            v.flowID == groupedValidationItem.selectedFlow
                    )
                    : this.validationItems.filter(
                        (v) =>
                            v.objectID == groupedValidationItem.objectID &&
                            v.flowID == groupedValidationItem.selectedFlow
                    )
            ).flatMap((g) => g.actions);
            const actions = [
                ...new Map(arr.map((item) => [item['defFieldCode'], item])).values(),
            ];
            actions.forEach((a) => {
                var field = groupedValidationItem.availableFields.find(
                    (x) => x.code === a.defFieldCode
                );
                if (field) {
                    var actionField = Utils.object.clone(new Field(), field);
                    if (!a.isParameterizable) {
                        actionField.value = a.value;
                        actionField.isReadOnly = true;
                    }
                    groupedValidationItem.actionFields.push(actionField);
                }
            });
            if (refreshList) {
                this.$nextTick(() => {
                    this.refreshList(
                        groupedValidationItem.actionFields.map((af) => af.code),
                        groupedValidationItem
                    );
                });
            }
            groupedValidationItem.loading = false;
        },
        getAvailableFlows() {
            var docIDs = this.documentList.map((d) => d.ObjectID);
            DocumentService.getAvailableFlows(this.transitionType, docIDs).then(
                (data) => {
                    if (
                        data.ctGroupedItems.every((g) => g.Flows.length == 1) &&
                        data.items.every((v) => v.Actions.length == 0) &&
                        !data.items.some((v) => v.RequiresCertificatePassword == true) &&
                        this.transitionType == 'V'
                    ) {
                        this.saveItem(data.items);
                    } else {
                        this.init(data);
                    }
                    this.show();
                }
            );
        },
        saveItem(itemsToSave) {
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }
            this.modalInstance.progress(true);
            if (!itemsToSave) {
                itemsToSave = [];
                var certificatePassword = this.certificatePassword;
                if (this.groupByContentType) {
                    for (let i = 0; i < this.ctGroupedItems.length; i++) {
                        const group = this.ctGroupedItems[i];
                        const items = this.validationItems
                            .filter(
                                (v) =>
                                    v.contentTypeID == group.contentTypeID &&
                                    v.flowID == group.selectedFlow
                            )
                            .map((x) => x.toDto());

                        items.forEach((item) => {
                            item.Comment = group.comment;
                            item.CertificatePassword = certificatePassword;
                            item.Actions.forEach((action) => {
                                var actionField = group.actionFields.find(
                                    (c) => c.code == action.DefFieldCode
                                );
                                if (actionField) {
                                    action.Value = actionField.toDto().Value;
                                }
                            });
                            itemsToSave.push(item);
                        });
                    }
                } else {
                    for (let i = 0; i < this.docGroupedItems.length; i++) {
                        const group = this.docGroupedItems[i];
                        const items = this.validationItems
                            .filter(
                                (v) =>
                                    v.objectID == group.objectID && v.flowID == group.selectedFlow
                            )
                            .map((x) => x.toDto());
                        items.forEach((item) => {
                            item.Comment = group.comment;
                            item.CertificatePassword = certificatePassword;
                            item.Actions.forEach((action) => {
                                const actionField = group.actionFields.find(
                                    (c) => c.code == action.DefFieldCode
                                );
                                if (actionField) {
                                    action.Value = actionField.toDto().Value;
                                }
                            });
                            itemsToSave.push(item);
                        });
                    }
                }
            }
            let message = '';
            if (this.transitionType == 'V') {
                message = this.$t('ValidatedSuccessfully');
            } else {
                message = this.$t('RejectedSuccessfully');
            }
            DocumentService.validate(itemsToSave)
                .then((result) => {
                    if (result) {
                        Utils.toast.success(
                            this.$t('Document') + '/' + this.$t('Documents') + ' ' + message
                        );
                        this.hide();
                        this.$emit('on-validated');
                    }
                })
                .catch(() => {
                    // error
                })
                .then(() => {
                    //always
                    this.modalInstance.progress(false);
                });
        },
        new(selectedDocuments, transitionType) {
            this.$v.$reset();
            this.groupByContentType = false;
            this.selectedTab = 0;
            this.validationItems = [];
            this.ctGroupedItems = [];
            this.docGroupedItems = [];
            this.loadedDocuments = [];
            this.documentList = selectedDocuments;
            this.transitionType = transitionType;
            this.getAvailableFlows();
        },
        show() {
            this.showModalOpen = true;
            this.loadDocument(this.docGroupedItems[this.selectedTab].objectID);
        },
        hide() {
            this.showModalOpen = false;
        },
        handleTabChange() {
            this.loadDocument(this.docGroupedItems[this.selectedTab].objectID);
        }
    }
};
</script>
